import { Input, ViewContainerRef, Component, ViewChild, OnInit, ChangeDetectorRef, ChangeDetectionStrategy, OnDestroy } from '@angular/core';
import { WidgetTypes } from '../../domain-models/enum/widget-types';
import { NumericWidgetComponent } from '../widget-list/numeric-widget/numeric-widget.component';
import { WidgetComponentInterface } from '../widget-list/widget-component.interface';
import { DashBoardLayoutOrchestratorViewModel } from '../../view-models/dash-board-layout.orchestrator-view-model';
import { DashBoardItemViewModel } from '../../view-models/dash-board-item.view-model';
import { OnOffWidgetComponent } from '../widget-list/onoff-widget/onoff-widget.component';
import { DonutWidgetComponent } from '../widget-list/donut-widget/donut-widget.component';
import { AlertWidgetComponent } from '../widget-list/alert-widget/alert-widget.component';
import { GaugeWidgetComponent } from '../widget-list/gauge-widget/gauge-widget.component';
import { EmptyGaugeWidgetComponent } from '../widget-list/empty-gauge-widget/empty-gauge-widget.component';
import { ProgramLinkWidgetComponent } from '../widget-list/program-link-widget/program-link-widget.component';
import { ResultSetWidgetComponent } from '../widget-list/result-set-widget/result-set-widget.component';
import { BarWidgetComponent } from '../widget-list/bar-widget/bar-widget.component';
import { TimeLineWidgetComponent } from '../widget-list/time-line-widget/time-line-widget.component';
import { DynamicTimeLineWidgetComponent } from '../widget-list/dynamic-time-line-widget/dynamic-time-line-widget.component';
import { IconResultSetWidgetComponent } from '../widget-list/icon-result-set-widget-new/icon-result-set-widget.component';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BaseTextBoxComponent, BaseToggleTextBoxComponent, FilledButtonComponent, LabelBoxComponent, ModalService, NTSTranslatePipe, ServiceResponse, TextButtonComponent } from '@nts/std';
import { ZoomResultWidgetComponent } from '../widget-list/zoom-result-widget/zoom-result-widget.component';
import { MenuItemShortcutWidgetComponent } from '../widget-list/menu-item-shortcut-widget/menu-item-shortcut-widget.component';
import { IdentityShortcutWidgetComponent } from '../widget-list/identity-shortcut-widget/identity-shortcut-widget.component';
import { AsyncPipe, NgFor, NgIf } from '@angular/common';
import { SvgIconComponent } from '@ngneat/svg-icon';
import { ColorPickerModule } from 'ngx-color-picker';
import { OverlayPanelModule } from 'primeng/overlaypanel';
import { SliderModule } from 'primeng/slider';
import { FormsModule } from '@angular/forms';
import { BehaviorSubject } from 'rxjs';

@UntilDestroy()
@Component({
  selector: 'nts-widget-host',
  templateUrl: './widget-host.component.html',
  styleUrls: ['./widget-host.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    NgIf,
    NgFor,
    SvgIconComponent,
    AsyncPipe,
    ColorPickerModule,
    TextButtonComponent,
    FilledButtonComponent,
    NTSTranslatePipe,
    OverlayPanelModule,
    SliderModule,
    FormsModule,
    LabelBoxComponent,
    BaseTextBoxComponent,
    BaseToggleTextBoxComponent
  ]
})
export class WidgetHostComponent implements OnInit, OnDestroy {

  @Input() vm: DashBoardItemViewModel;
  @Input() orchestratorViewModel: DashBoardLayoutOrchestratorViewModel;

  @ViewChild('widgetPlaceHolder', { read: ViewContainerRef, static: true })

  widgetPlaceHolder: ViewContainerRef;
  widgetInstance: WidgetComponentInterface;
  componentLoaded = false;
  timerTitle = "DashBoardTimerTitle";
  maxTimer: number = 20;
  minTimer: number = 2;
  timerValue: number = this.minTimer;
  disableSettingsConfirm = false;
  timerToggleTitle = "DashBoardTimerToggleTitle";

  colorPicker$ = new BehaviorSubject<string>(null);
  showColorPicker$ = new BehaviorSubject<boolean>(false);
  disableConfirm$ = new BehaviorSubject<boolean>(false);
  timerToggleValue$ = new BehaviorSubject<boolean>(false);
  loading$ = new BehaviorSubject<boolean>(true);

  mouseIsOverWidget = false;
  componentsMapping = {
    [WidgetTypes.OnOff]: OnOffWidgetComponent,
    [WidgetTypes.Alert]: AlertWidgetComponent,
    [WidgetTypes.Number]: NumericWidgetComponent,
    [WidgetTypes.ProgramLink]: ProgramLinkWidgetComponent,
    [WidgetTypes.Gauge]: GaugeWidgetComponent,
    [WidgetTypes.EmptyGauge]: EmptyGaugeWidgetComponent,
    [WidgetTypes.ResultSet]: ResultSetWidgetComponent,
    [WidgetTypes.TimeLine]: TimeLineWidgetComponent,
    [WidgetTypes.Bar]: BarWidgetComponent,
    [WidgetTypes.Donut]: DonutWidgetComponent,
    [WidgetTypes.DynamicTimeline]: DynamicTimeLineWidgetComponent,
    [WidgetTypes.IconResultSet]: IconResultSetWidgetComponent,
    [WidgetTypes.SearchResults]: ZoomResultWidgetComponent,
    [WidgetTypes.MenuItemShortcut]: MenuItemShortcutWidgetComponent,
    [WidgetTypes.IdentityShortcut]: IdentityShortcutWidgetComponent
  };

  #saveColorPicker: boolean = false;
  #errorResponse: ServiceResponse;
  #initColorPicker: string = null;


  constructor(
    private cd: ChangeDetectorRef,
    private modalService: ModalService
  ) {

  }

  ngOnDestroy(): void {
    this.#saveColorPicker = null;
    this.#errorResponse = null;
    this.#initColorPicker = null;

    this.colorPicker$.complete();
    this.colorPicker$ = null;

    this.showColorPicker$.complete();
    this.showColorPicker$ = null;

    this.disableConfirm$.complete();
    this.disableConfirm$ = null;

    this.timerToggleValue$.complete();
    this.timerToggleValue$ = null;

    this.loading$.complete();
    this.loading$ = null;
  }

  async initialize(): Promise<void> {

    this.vm.widgetRef.dragStarted.pipe(
      untilDestroyed(this)
    ).subscribe(() => {
      this.showColorPicker$.next(false);
    });

    this.vm.widgetRef.loadingErrorChanged.pipe(
      untilDestroyed(this)
    ).subscribe((response) => {
      this.#errorResponse = response.operationSuccedeed ? null : response;
      this.widgetInstance.hasError$.next(!response.operationSuccedeed);
    });

    this.widgetInstance.forceHideLoader.pipe(
      untilDestroyed(this)
    ).subscribe(() => {
      this.loading$.next(false);
    });

    this.widgetInstance.forceShowLoader.pipe(
      untilDestroyed(this)
    ).subscribe(() => {
      this.loading$.next(true);
    });

    await this.widgetInstance.initialize();

    this.colorPicker$.next(await this.widgetInstance.getCurrentWidgetColor());
    const localTimerValue = await this.widgetInstance.getCurrentWidgetTimer();
    this.timerValue = localTimerValue || localTimerValue == 0 ? this.minTimer : localTimerValue;
    this.timerToggleValue$.next(await this.widgetInstance.getCurrentWidgetTimerToggle() ?? false);

    this.#initColorPicker = this.colorPicker$.value;

    this.cd.markForCheck();
  }

  async ngOnInit() {
  }

  async confirmPickerClick() {
    if (!this.disableConfirm$.value) {
      this.#saveColorPicker = true;
      this.showColorPicker$.next(false);
    } else {
      this.#saveColorPicker = false;
    }
  }

  async confirmSettingsClick() {
    this.widgetInstance.settingsChanged.next({
      timer: this.timerValue,
      timerToggle: this.timerToggleValue$.value
    });
  }

  removeWidget() {
    this.orchestratorViewModel.rootViewModel.content.removeByIdentity(this.vm.getDomainModel().currentIdentity);
    this.cd.markForCheck();
  }

  errorDetails() {
    this.modalService.showPopUp(this.#errorResponse.informations, this.#errorResponse.errors);
  }

  updateColorFromColorPicker(newColor: string) {
    this.colorPicker$.next(newColor);
    if (this.widgetInstance.checkIfColorIsValid(newColor)) {
      this.disableConfirm$.next(false);
      this.widgetInstance.colorChanged.next(newColor);
    } else {
      this.disableConfirm$.next(true);
    }
  }

  cancelPickerClick() {
    this.#saveColorPicker = false;
    this.showColorPicker$.next(false);
  }

  onClosingPicker() {
    if (this.#saveColorPicker) {
      this.#initColorPicker = this.colorPicker$.value;
    } else {
      this.colorPicker$.next(this.#initColorPicker);
      this.widgetInstance.colorChanged.next(this.colorPicker$.value);
    }

    this.#saveColorPicker = false;
  }

  onToggleChange(newValue: boolean) {
    this.timerToggleValue$.next(newValue);
  }

  onWidgetClick() {
    this.showColorPicker$.next(false);
    this.widgetInstance.newUpdatedDataAvailable$.next(false);
  }
}
